/**
 * URL용 ID 인코딩/디코딩 유틸리티.
 * 순차적 ID 추측을 통한 리소스 열거를 방지한다.
 * 암호학적 보안은 아님 — 실제 접근 제어는 백엔드 인증으로 처리.
 */

const OFFSET = 83917;
const MULTIPLIER = 37;
const CHARSET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
const BASE = CHARSET.length;

/**
 * 숫자 ID를 난독화된 문자열로 인코딩.
 * 예: 123 -> "fPe"
 */
export const encodeId = (id: number): string => {
  let n = (id * MULTIPLIER + OFFSET) >>> 0; // unsigned 32-bit
  if (n === 0) return CHARSET[0];
  let result = '';
  while (n > 0) {
    result = CHARSET[n % BASE] + result;
    n = Math.floor(n / BASE);
  }
  return result;
};

/**
 * 난독화된 문자열을 원래 숫자 ID로 디코딩.
 * 유효하지 않은 입력이면 null 반환.
 */
export const decodeId = (encoded: string): number | null => {
  if (!encoded || encoded.length === 0) return null;

  let n = 0;
  for (const char of encoded) {
    const idx = CHARSET.indexOf(char);
    if (idx === -1) return null;
    n = n * BASE + idx;
  }

  const id = (n - OFFSET) / MULTIPLIER;
  if (!Number.isInteger(id) || id < 0) return null;
  return id;
};

/**
 * 인코딩된 ID인지 확인 (숫자만으로 이루어진 plain ID와 구분).
 */
export const isEncodedId = (str: string): boolean => {
  return !/^\d+$/.test(str);
};

/**
 * 라우트 파라미터를 숫자 ID로 변환.
 * 인코딩된 문자열과 plain 숫자 ID 모두 지원 (하위 호환).
 */
export const resolveId = (param: string): number | null => {
  if (/^\d+$/.test(param)) {
    return parseInt(param, 10);
  }
  return decodeId(param);
};
